/**
 * useEncounterAuditDetail Hook
 *
 * Fetches encounter audit detail with encounter analysis and all note audits
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import {
  useGetAdminEncounterDetailQuery,
  useGetEncounterAuditDetailQuery,
} from '../store/api';
import type { EncounterAuditDetail } from '../types';

interface UseEncounterAuditDetailOptions {
  /** Encounter ID to fetch */
  encounterId?: string;
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
}

interface UseEncounterAuditDetailResult {
  /** Full encounter audit detail */
  encounter: EncounterAuditDetail | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether data is being fetched (including refetches) */
  isFetching: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
}

export const useEncounterAuditDetail = (
  options: UseEncounterAuditDetailOptions = {},
): UseEncounterAuditDetailResult => {
  const { encounterId, organizationId } = options;

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminEncounterDetailQuery(
    organizationId && encounterId ? { encounterId, organizationId } : skipToken,
  );

  const providerQuery = useGetEncounterAuditDetailQuery(
    !organizationId && encounterId ? { encounterId } : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, isFetching, error, refetch } = organizationId
    ? adminQuery
    : providerQuery;

  const encounter = useMemo(() => data || null, [data]);

  return {
    encounter,
    isLoading,
    isFetching,
    error: error ? 'Failed to load encounter audit detail' : null,
    refetch,
  };
};

export default useEncounterAuditDetail;
